import { type RefObject, useRef } from "react";
import { toast } from "sonner";
import { ArrowRight } from "@/components/animate-ui/icons/arrow-right";
import { Plus } from "@/components/animate-ui/icons/plus";
import { RotateCw } from "@/components/animate-ui/icons/rotate-cw";
import { SquareX } from "@/components/animate-ui/icons/square-x";
import { X } from "@/components/animate-ui/icons/x";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupTextarea,
} from "@/components/ui/input-group";
import { cn } from "@/lib/utils";
import { formatFileSize, validateFiles } from "@/lib/utils/file-validation";
import type { Tool } from "@/lib/utils/task-form";
import { PermissionsDropdown } from "./permissions-dropdown";
import { ToolsDropdown } from "./tools-dropdown";

type PermissionMode = "yolo" | "safe" | "plan";

interface TaskInputFormProps {
	instructions: string;
	placeholder: string;
	isStreaming: boolean;
	placeholderVisible: boolean;
	selectedTools: string[];
	combinedTools: Tool[];
	textareaRef: RefObject<HTMLTextAreaElement | null>;
	onSubmit: (e: React.FormEvent) => void;
	onInstructionsChange: (value: string) => void;
	onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	onFocus: () => void;
	onBlur: () => void;
	onToggleTool: (toolId: string) => void;
	onStop: () => Promise<void> | void;
	autoFocus?: boolean;
	variant?: "default" | "chat";
	showRerunButton?: boolean;
	onRerun?: () => void;
	disabled?: boolean;
	disabledMessage?: string;
	selectedFiles?: File[];
	onFilesChange?: (files: File[]) => void;
	permissionMode?: PermissionMode;
	onPermissionModeChange?: (value: PermissionMode) => void;
}

const WORD_THRESHOLD = 100; // Auto-attach text as file if > 100 words
// Explicit image types (not image/*) + code files to match backend allowlist
const FILE_ACCEPT = ".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt,.md,.csv,.xlsx,.docx,.py,.js,.json";

/**
 * Task input form with textarea, tools selection, and submit/stop controls.
 *
 * Displays differently based on variant: "default" for initial view with shadow effects,
 * "chat" for streaming view with simpler styling. Includes attachment button (when onFilesChange
 * is provided), integration dropdown, and context-aware submit/stop button.
 */
export function TaskInputForm({
	instructions,
	placeholder,
	isStreaming,
	placeholderVisible,
	selectedTools,
	combinedTools,
	textareaRef,
	onSubmit,
	onInstructionsChange,
	onKeyDown,
	onFocus,
	onBlur,
	onToggleTool,
	onStop,
	autoFocus = false,
	variant = "default",
	showRerunButton = false,
	onRerun,
	disabled = false,
	disabledMessage,
	selectedFiles = [],
	onFilesChange,
	permissionMode = "safe",
	onPermissionModeChange,
}: TaskInputFormProps) {
	const isDefaultVariant = variant === "default";
	const isDisabled = disabled || isStreaming;
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files || []);
		if (files.length === 0) return;

		const allFiles = [...selectedFiles, ...files];
		const validation = validateFiles(allFiles);
		if (!validation.valid) {
			toast.error(validation.error);
			return;
		}
		onFilesChange?.(allFiles);
		// Reset input so same file can be selected again
		e.target.value = "";
	};

	const handleRemoveFile = (index: number) => {
		onFilesChange?.(selectedFiles.filter((_, i) => i !== index));
	};

	const handlePaste = (e: React.ClipboardEvent) => {
		if (!onFilesChange) return;

		const clipboardData = e.clipboardData;
		const newFiles: File[] = [];

		// Check for images in clipboard
		for (const item of clipboardData.items) {
			if (item.type.startsWith("image/")) {
				const file = item.getAsFile();
				if (file) {
					const ext = item.type.split("/")[1] || "png";
					const namedFile = new File([file], `pasted-image-${Date.now()}.${ext}`, {
						type: file.type,
					});
					newFiles.push(namedFile);
				}
			}
		}

		// Check for long text (> 150 words) - convert to .txt file
		const text = clipboardData.getData("text/plain");
		if (text && newFiles.length === 0) {
			const wordCount = text.trim().split(/\s+/).length;
			if (wordCount > WORD_THRESHOLD) {
				const textFile = new File([text], `pasted-text-${Date.now()}.txt`, {
					type: "text/plain",
				});
				newFiles.push(textFile);
				e.preventDefault(); // Don't also paste into textarea
			}
		}

		if (newFiles.length > 0) {
			e.preventDefault();
			const allFiles = [...selectedFiles, ...newFiles];
			const validation = validateFiles(allFiles);
			if (!validation.valid) {
				toast.error(validation.error);
				return;
			}
			onFilesChange(allFiles);
		}
	};

	return (
		<form onSubmit={onSubmit}>
			<InputGroup
				className={cn(
					"overflow-hidden rounded-xl border bg-gradient-to-b from-card to-card/90",
					isDefaultVariant
						? "border-border/85 shadow-sm transition-[border-color,box-shadow,background-color] duration-200 hover:border-border hover:shadow-md focus-within:border-accent-blue-border focus-within:shadow-lg focus-within:ring-2 focus-within:ring-accent-blue/20 dark:border-border/70 dark:hover:border-border/90 dark:focus-within:border-accent-blue-border/70 dark:focus-within:ring-accent-blue/25"
						: "border-transparent shadow-none focus-within:ring-0"
				)}
			>
				<InputGroupTextarea
					ref={textareaRef}
					placeholder={disabledMessage ?? placeholder}
					value={instructions}
					onChange={(e) => onInstructionsChange(e.target.value)}
					onKeyDown={onKeyDown}
					onFocus={onFocus}
					onBlur={onBlur}
					onPaste={handlePaste}
					disabled={isDisabled}
					autoFocus={autoFocus}
					className={cn(
						"flex-none resize-none overflow-y-auto px-4 py-3 text-[15px] leading-6 transition-all duration-200 sm:text-base",
						isDefaultVariant
							? "[&::placeholder]:transition-opacity [&::placeholder]:duration-200"
							: "transition-opacity duration-200",
						placeholderVisible
							? isDefaultVariant
								? "[&::placeholder]:opacity-100"
								: "opacity-100"
							: isDefaultVariant
								? "[&::placeholder]:opacity-0"
								: "opacity-80"
					)}
				/>

				{/* Selected files display */}
				{selectedFiles.length > 0 && (
					<div className="flex flex-wrap gap-2 border-t border-border/70 bg-muted/20 px-3 py-2">
						{selectedFiles.map((file, i) => (
							<Badge key={`${file.name}-${i}`} variant="secondary" className="gap-1 pr-1">
								<span className="max-w-[150px] truncate">{file.name}</span>
								<span className="text-muted-foreground text-[10px]">
									({formatFileSize(file.size)})
								</span>
								<button
									type="button"
									onClick={() => handleRemoveFile(i)}
									className="ml-0.5 hover:bg-muted rounded p-0.5"
									aria-label={`Remove ${file.name}`}
								>
									<X className="size-3" />
								</button>
							</Badge>
						))}
					</div>
				)}

				<InputGroupAddon
					align="end"
					className={cn(
						"border-border/70 bg-muted/25 px-2 py-2 sm:px-3",
						isDefaultVariant ? "border-t" : "border-l"
					)}
				>
					<div className="flex w-full items-center justify-between gap-2">
						{/* Left: Attachments & Tools */}
						<div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
							{/* Attachments button */}
							{onFilesChange && (
								<>
									<InputGroupButton
										variant="outline"
										className="rounded-full"
										size="icon-sm"
										type="button"
										aria-label="Add attachments"
										onClick={() => fileInputRef.current?.click()}
										disabled={isDisabled}
									>
										<Plus className="size-4" animateOnHover />
									</InputGroupButton>
									<input
										ref={fileInputRef}
										type="file"
										multiple
										accept={FILE_ACCEPT}
										onChange={handleFileSelect}
										className="hidden"
									/>
								</>
							)}

							{/* Integrations dropdown */}
							<ToolsDropdown
								selectedTools={selectedTools}
								combinedTools={combinedTools}
								onToggleTool={onToggleTool}
							/>

							{onPermissionModeChange && (
								<PermissionsDropdown
									value={permissionMode}
									onChange={onPermissionModeChange}
									disabled={disabled}
								/>
							)}
						</div>

						{/* Right: Keyboard hint & Action buttons */}
						<div className="flex items-center gap-2 sm:gap-3">
							{/* Keyboard hint - only show when not streaming */}
							{!isStreaming && (
								<div className="hidden items-center gap-1.5 text-xs text-muted-foreground md:flex">
									<kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100">
										<span className="text-xs">⌘</span>↵
									</kbd>
									<span>to submit</span>
								</div>
							)}

							{/* Streaming indicator when active */}
							{isStreaming && (
								<span className="hidden text-xs text-muted-foreground sm:inline">
									Agent is working...
								</span>
							)}

							{/* Submit button (when not streaming) or Stop button (when streaming) */}
							{isStreaming ? (
								<Button
									type="button"
									size="icon-lg"
									variant="destructive"
									onClick={async () => {
										await onStop();
									}}
									aria-label="Stop execution"
									className="shrink-0 shadow-sm"
								>
									<SquareX animateOnHover className="size-4" />
								</Button>
							) : (
								<Button
									type="submit"
									size="icon-lg"
									variant="default"
									disabled={isDisabled || !instructions.trim()}
									aria-label="Automate task"
									className="btn-glow-blue shrink-0 shadow-sm hover:shadow-md"
								>
									<ArrowRight className="size-4" animateOnHover />
								</Button>
							)}

							{/* Re-run button - show when task completed */}
							{showRerunButton && !isStreaming && (
								<Button
									type="button"
									size="icon"
									variant="ghost"
									onClick={onRerun}
									aria-label="Re-run task"
									className="shrink-0 text-muted-foreground hover:text-foreground"
								>
									<RotateCw className="size-4" animateOnHover />
								</Button>
							)}
						</div>
					</div>
				</InputGroupAddon>
			</InputGroup>
		</form>
	);
}
